/*
 * Metadata for the First Round marketing pages (firstround.com.au). Shared by the server, which
 * renders it into the <head> for crawlers (apps/server/src/brand-sites), and the client, which
 * routes the pages and keeps the tab title right after in-app navigation.
 *
 * To add an industry page (e.g. "/for-hospitality"): add it to FIRST_ROUND_INDUSTRY_PAGES and
 * register its component in FIRST_ROUND_INDUSTRY_COMPONENTS in apps/client/src/router/index.tsx.
 * The server then serves it with its own <head>, lists it in sitemap.xml and stops redirecting it.
 */

export type BrandPageMeta = { title: string; description: string };

export const FIRST_ROUND_HOME_META: BrandPageMeta = {
  title: "First Round | AI Interview Practice",
  description:
    "Practise realistic AI-powered interviews and get feedback on your answers and delivery. Free to try, no card needed.",
};

export const FIRST_ROUND_INDUSTRY_PAGES: Record<string, BrandPageMeta> = {
  "/for-educational-institutes": {
    title: "First Round for Australian educational institutes — career preparation",
    description:
      "Tailored resumes, interview practice and Spotlight Intros for your students. Explore an institutional pilot with cohort reporting shaped around your team.",
  },
};

/*
 * The First Round legal set, served from firstround.com.au (and at /first-round/<path> on staging
 * and localhost). "/legal" is the hub that lists the other four. Each page renders a markdown file
 * in apps/client/src/pages/first-round/legal/docs; the client registry there must cover exactly
 * these paths (legal.test.ts fails if the two drift).
 *
 * Adding a document: add its path here, its .md file and an entry in the client registry.
 */
export const FIRST_ROUND_LEGAL_PAGES = {
  "/legal": {
    title: "First Round legal documents",
    description:
      "The terms, privacy policy and trial notices that apply to First Round, our AI practice interview service.",
  },
  "/terms": {
    title: "First Round Terms of Service",
    description:
      "The terms that apply when you create a First Round account and sit paid practice interviews.",
  },
  "/privacy": {
    title: "First Round Privacy Policy",
    description:
      "How First Round collects, uses, stores and discloses your personal information, including how AI processes your interviews.",
  },
  "/trial-terms": {
    title: "First Round Trial Interview Terms",
    description: "The terms for trying First Round's free AI practice interview.",
  },
  "/trial-privacy": {
    title: "First Round Trial Interview Privacy Collection Notice",
    description:
      "What personal information we collect when you try First Round's free trial interview, and why.",
  },
} as const satisfies Record<string, BrandPageMeta>;

export type FirstRoundLegalPath = keyof typeof FIRST_ROUND_LEGAL_PAGES;
